import { ChangeDetectionStrategy, Component, InjectionToken, OnInit, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CdkDialogContainer, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { DialogSurfaceComponent } from './dialog-surface';
import { DialogVariant } from './dialog-types';

/** Chrome params supplied by `DialogService` to the container via {@link CMP_DIALOG_CHROME}. */
export interface DialogChrome {
  variant: DialogVariant;
  title: string;
  description: string;
  icon: string;
  dismissible: boolean;
  closeLabel: string;
  width: string;
  showActions: boolean;
  titleId: string;
  descId: string;
}

/**
 * Chrome params injected into `CmpDialogContainer`. Provided via the CDK `container` object
 * form (`{ type, providers }`) rather than spread onto the `DialogConfig` — that avoids
 * clobbering real config fields (e.g. `width`, which CDK also maps onto the overlay pane).
 */
export const CMP_DIALOG_CHROME = new InjectionToken<DialogChrome>('CMP_DIALOG_CHROME');

/**
 * Container used by `DialogService` via CDK `Dialog.open(content, { container })`. Extends
 * the CDK dialog container (focus trap, focus capture/restore, portal outlet) and renders the
 * shared dialog surface around the attached content. The opened content is attached into the
 * surface's body via the `cdkPortalOutlet`; chrome params (title/variant/etc.) ride on the
 * `DialogConfig`. Internal — not part of the public API.
 *
 * Host aria bindings are redeclared here (component host metadata is not inherited) and read
 * from the injected `DialogConfig`, matching `CdkDialogContainer`'s own bindings.
 */
@Component({
  selector: 'cmp-dialog-container',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DialogSurfaceComponent, CdkPortalOutlet],
  template: `
    <cmp-dialog-surface
      [variant]="chrome.variant"
      [width]="chrome.width"
      [title]="chrome.title"
      [description]="chrome.description"
      [icon]="chrome.icon"
      [dismissible]="chrome.dismissible"
      [closeLabel]="chrome.closeLabel"
      [showActions]="chrome.showActions"
      [titleId]="chrome.titleId"
      [descId]="chrome.descId"
      (closeClick)="dialogRef.close()"
    >
      <ng-template cdkPortalOutlet></ng-template>
    </cmp-dialog-surface>
  `,
  host: {
    class: 'cmp-dialog-container',
    tabindex: '-1',
    '[attr.role]': 'config.role',
    '[attr.aria-modal]': 'config.ariaModal',
    '[id]': 'config.id',
    '[attr.aria-labelledby]': 'config.ariaLabel ? null : config.ariaLabelledBy',
    '[attr.aria-label]': 'config.ariaLabel',
    '[attr.aria-describedby]': 'config.ariaDescribedBy',
  },
})
export class CmpDialogContainer extends CdkDialogContainer implements OnInit {
  /** The dialog config (provided by CDK to the container injector); drives the aria host bindings. */
  protected readonly config = inject(DialogConfig);
  /** Ref for the open dialog (provided by CDK to the container injector); closes on the ✕. */
  protected readonly dialogRef = inject<DialogRef<unknown, unknown>>(DialogRef);
  /** Chrome params supplied by `DialogService` via the container `providers`. */
  protected readonly chrome = inject(CMP_DIALOG_CHROME);

  private readonly document = inject(DOCUMENT);
  private _previousOverflowY = '';

  ngOnInit() {
    this._previousOverflowY = this.document.documentElement.style.overflowY;
    this.document.documentElement.style.overflowY = 'hidden';
  }

  override ngOnDestroy() {
    this.document.documentElement.style.overflowY = this._previousOverflowY;
    super.ngOnDestroy();
  }
}
